import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

class ProductInventory extends Component {
  #listeners;
  #request;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    const signal = this.#listeners.signal;
    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.handleProductSelect, { signal });
    if (this.dataset.vvLiveStock === 'true') {
      document.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, { signal });
      document.addEventListener('visibilitychange', this.handleVisibility, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
    this.#generation++;
  }

  updateInventory(html) {
    const selector = this.dataset.blockId ? 'product-inventory[data-block-id="' + CSS.escape(this.dataset.blockId) + '"]' : 'product-inventory';
    const next = html.querySelector(selector);
    if (!next) return;
    morph(this, next, { childrenOnly: true });
    this.dataset.productId = next.dataset.productId;
    this.dataset.variantId = next.dataset.variantId;
  }

  /** @param {Event & {promise?: Promise<{detail?: {html?: Document, productId?: string, newProduct?: unknown}}>}} event */
  handleProductSelect = async event => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    this.#request?.abort();
    const generation = ++this.#generation;
    try {
      const result = await event.promise;
      const detail = result?.detail;
      if (!this.isConnected || generation !== this.#generation || !detail?.html) return;
      if (!detail.newProduct && detail.productId && String(detail.productId) !== this.dataset.productId) return;
      this.updateInventory(detail.html);
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[product-inventory] Could not update inventory.', error);
    }
  };

  /** @param {Event & {promise?: Promise<unknown>}} event */
  handleCartUpdate = async event => {
    try {
      await event.promise;
      if (this.isConnected) await this.refreshStock();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[product-inventory] Could not refresh stock.', error);
    }
  };

  handleVisibility = () => { if (!document.hidden) this.refreshStock(); };

  async refreshStock() {
    const sectionId = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
    if (!sectionId || !this.dataset.variantId) return;
    this.#request?.abort();
    this.#request = new AbortController();
    const generation = ++this.#generation;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.dataset.variantId);
    try {
      const source = await sectionRenderer.getSectionHTML(sectionId, false, url, this.#request.signal);
      if (!this.isConnected || generation !== this.#generation) return;
      this.updateInventory(new DOMParser().parseFromString(source, 'text/html'));
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[product-inventory] Could not refresh stock.', error);
    }
  }
}
if (!customElements.get('product-inventory')) customElements.define('product-inventory', ProductInventory);
